import { Component, Event, type EventEmitter, h, Prop, State } from '@stencil/core';
import { ITEMS_PER_PAGE } from '../../../constants/paginationConstants';
import type { SearchApiDocument } from '../../../api/model/response/documents/SearchResult';
import type { ReflectedTab } from '../../SearchCompletedEvent';
import type { PaginationEvent } from '../search-tab/PaginationEvent';
import { makeLogger } from '../../../logger/logger';
import type { labelProviderInterface } from '../../LabelProviderInterface';

const { info, warn } = makeLogger({
  pathToCurrentFile: 'components/result-page/tabs-pagination/tabs-pagination.tsx',
  loggersList: ['info', 'warn']
});

@Component({
  tag: 'uiaas-tabs-pagination',
  styleUrl: 'tabs-pagination.scss',
})
export class UiaasTabsPagination {
    @Prop() readonly perPage: number = ITEMS_PER_PAGE;

    @Prop() readonly currentPage: number;

    @Prop() readonly queryResult;

    @Prop() readonly query;

    @Prop() readonly paginationChanged;

    @State() chatBoxIsVisible = false;

    @Prop() readonly hideQuery: boolean = false;

    @Prop() readonly aggregatedResults: SearchApiDocument[];

    @Prop({ mutable: true }) labelProvider?: labelProviderInterface;

    @Prop() readonly reflectedState: ReflectedTab;

    @Prop() readonly disableFiltersOnLoading: boolean;

    @Event({
      eventName: 'search:PaginationLoadMoreClick',
      composed: true,
      cancelable: true,
      bubbles: true,
    })
      paginationEmmiter: EventEmitter<PaginationEvent>;

    @State()
      listWasScrolledToTop = true;

    private handlePaginationWasChanged = () => {
        this.paginationChanged({
          detail: {
            value: this.currentPage + 1,
            perPage: this.perPage,
          },
        });
        this.paginationEmmiter.emit({
          event: 'loadMore',
          tabName: this.reflectedState?.tab,
          perPage: this.perPage,
          numberOfAlreadyDisplayedResults: this.aggregatedResults?.length,
        });
        this.listWasScrolledToTop = false;
        this.handleChatbox();
    };

    componentWillLoad() {
        if (!this.labelProvider) {
          info('Passing default LabelProvider for ShowMore');

          this.labelProvider = (name, fallback) => {
              warn('labelProvider not defined for ShowMore', [
                name,
                fallback,
              ]);
              return fallback;
          };
        }
    }

    private handleChatbox = () => {
        const chatBox = document.querySelector('#chatStickyButton');
        if (chatBox) {
          this.chatBoxIsVisible = true;
        }
    };

    private isMoreButtonMustShows(): boolean {
        return (
          this.aggregatedResults.length !==
                this.queryResult.searchResults.totalHits &&
            this.perPage < this.queryResult.searchResults.totalHits
        );
    }

    render() {
        if (this.aggregatedResults?.length) {
          return (
            <div class={'pagination__container'}>
              <slot name="before-pagination" />
              <div class={'show-more__container'}>
                <p class={'show-more__text'}>
                  {`1 - ${
                    this.aggregatedResults
                      ? this.aggregatedResults.length
                      : null
                  } ${this.labelProvider('load-more.of', 'of')} ${
                    this.queryResult.searchResults.totalHits
                  }
                  ${this.hideQuery ? this.labelProvider('results', 'results') : this.getQuery()}`}
                </p>
                {this.isMoreButtonMustShows() && (
                  <qds-button
                    disabled={
                      this.disableFiltersOnLoading &&
                                    this.reflectedState?.ajaxIsLoading
                    }
                    class={'show-more__button'}
                    size="large"
                    onClick={this.handlePaginationWasChanged}
                    text={ this.labelProvider('load-more', 'Load more')}
                  >

                  </qds-button>
                )}
              </div>
              <slot name="after-pagination" />
            </div>
          );
        }
    }
    private getQuery() {
        return `${this.labelProvider('results-for', 'results for')} "${this.query}"`;
    }
}
